import { MouseEvent as ReactMouseEvent, MutableRefObject, useEffect, useRef } from 'react'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'

import { getCellId } from '@components/TimeLineGrid/helpers'

import { useExecuteCommandMutation } from '@redux/api'
import {
  addLoadingItemId,
  clearCellItemDragState,
  removeLoadingItemId,
  setCellItemDragState,
} from '@redux/reducers/timeLine.reducer'
import { AppDispatch } from '@redux/store'

import { getCommandRequestData } from '@helpers'
import {
  ACTION_CODE,
  EVENT_CODE,
  TIME_LINE_OBJECT_X_CELL_DRAG_SOURCE_CLASS_NAME,
  TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_CLASS_NAME,
  TIMELINE_CELL_DATA_ATTR,
} from '@constants'
import {
  ActionRow,
  ObjectDataRecord,
  SelectedCell,
  TimelineCellContext,
  TimelineCellItemDragState,
  TimelineCellMove,
  TimelineDragCellsRefState,
  TimeLineDTOType,
  TimelineGhostCell,
  TimeLineGridDataRecord,
} from '@types'

const DRAG_THRESHOLD_PX = 4

function findCellElement(cellId: string): HTMLElement | null {
  const nodes = document.querySelectorAll(`[${TIMELINE_CELL_DATA_ATTR}]`)

  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i] as HTMLElement

    if (el.getAttribute(TIMELINE_CELL_DATA_ATTR) === cellId) {
      return el
    }
  }

  return null
}

export type UseCellItemDragAndDropParams = {
  cellContextRef: MutableRefObject<TimelineCellContext>
  timeLineGridData: TimeLineGridDataRecord[]
  config: TimeLineDTOType
  objectXPKField?: { name: string }
  handleMoveCellsBetweenPositions: (moves: TimelineCellMove[]) => boolean
  handleExecuteActions: (data: {
    actions: ActionRow[]
    srcObj: ObjectDataRecord
    eventCode: EVENT_CODE
  }) => Promise<PromiseSettledResult<unknown>[] | undefined>
  handleClearSelectedCells: () => void
  executeCommand: ReturnType<typeof useExecuteCommandMutation>[0]
  dispatch: AppDispatch
}

export const useCellItemDragAndDrop = (params: UseCellItemDragAndDropParams) => {
  const {
    cellContextRef,
    timeLineGridData,
    objectXPKField,
    handleMoveCellsBetweenPositions,
    handleExecuteActions,
    handleClearSelectedCells,
    config,
    executeCommand,
    dispatch,
  } = params

  const dragStateRef = useRef<TimelineCellItemDragState | null>(null)

  const dragCellsRef = useRef<TimelineDragCellsRefState>({
    source: [],
    target: [],
    dragStartCell: null,
  })

  const targetEnterCountRef = useRef(new Map<string, number>())

  const timeLineGridDataRef = useRef(timeLineGridData)
  const moveHandlerRef = useRef(handleMoveCellsBetweenPositions)
  const objectXPKFieldRef = useRef(objectXPKField)
  const executeActionsRef = useRef(handleExecuteActions)
  const clearSelectionRef = useRef(handleClearSelectedCells)

  timeLineGridDataRef.current = timeLineGridData
  moveHandlerRef.current = handleMoveCellsBetweenPositions
  objectXPKFieldRef.current = objectXPKField
  executeActionsRef.current = handleExecuteActions
  clearSelectionRef.current = handleClearSelectedCells

  const prevBodyStylesRef = useRef<{ userSelect: string; cursor: string } | null>(null)

  const applyDragBodyStyles = () => {
    prevBodyStylesRef.current = {
      userSelect: document.body.style.userSelect,
      cursor: document.body.style.cursor,
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  const restoreDragBodyStyles = () => {
    if (prevBodyStylesRef.current) {
      document.body.style.userSelect = prevBodyStylesRef.current.userSelect
      document.body.style.cursor = prevBodyStylesRef.current.cursor
      prevBodyStylesRef.current = null
    }
  }

  const restoreAllCellStyles = () => {
    dragCellsRef.current.source.forEach(item => {
      item.htmlElement?.classList.remove(TIME_LINE_OBJECT_X_CELL_DRAG_SOURCE_CLASS_NAME)
    })

    dragCellsRef.current.target.forEach(item => {
      item?.htmlElement?.classList.remove(TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_CLASS_NAME)
    })

    dragCellsRef.current = {
      target: [],
      source: [],
      dragStartCell: null,
    }

    targetEnterCountRef.current.clear()
  }

  const buildGhostCells = (data: {
    anchorRect: DOMRect
    anchorCell: {
      rowId: string
      columnId: string
      events: ObjectDataRecord[]
      rowData: ObjectDataRecord
      item: ObjectDataRecord
      isSelected: boolean
    }
  }): TimelineGhostCell[] => {
    const { anchorCell, anchorRect } = data

    const events = anchorCell.isSelected
      ? cellContextRef.current.selectedCells
      : {
          [getCellId(anchorCell.rowId, anchorCell.columnId)]: {
            columnId: anchorCell.columnId,
            rowId: anchorCell.rowId,
            rowData: anchorCell.rowData,
            events: [anchorCell.item],
          },
        }

    const cells: TimelineGhostCell[] = []

    for (const [cellId, selected] of Object.entries(events)) {
      const el = findCellElement(cellId)

      if (!el || !selected.events?.length) {
        continue
      }

      const r = el.getBoundingClientRect()

      cells.push({
        relX: r.left - anchorRect.left,
        relY: r.top - anchorRect.top,
        width: r.width,
        height: r.height,
        rowData: selected.rowData,
        columnId: selected.columnId,
        rowId: selected.rowId,
        events: [...selected.events],
      })
    }

    return cells
  }

  const endDragSession = () => {
    restoreAllCellStyles()
    restoreDragBodyStyles()
    dragStateRef.current = null
    dispatch(clearCellItemDragState())
    removeWindowListenersRef.current?.()
    removeWindowListenersRef.current = null
    setTimeout(() => {
      cellContextRef.current = {
        ...cellContextRef.current,
        isDraggingActive: false,
      }
    })
  }

  const removeWindowListenersRef = useRef<(() => void) | null>(null)

  const onDrop = () => {
    const dropAction = config.objectX.actions?.find(item => item.eventCode === EVENT_CODE.ON_DROP)

    if ((!dragStateRef.current && !dragCellsRef.current.source.length) || !dropAction) {
      endDragSession()

      return
    }

    if (dragCellsRef.current.target.length && objectXPKFieldRef.current?.name) {
      const moves: TimelineCellMove[] = []

      dragCellsRef.current.source.forEach((sourceCell, sourceCellIndex) => {
        const targetCell = dragCellsRef.current.target[sourceCellIndex]

        if (targetCell) {
          const events = sourceCell?.isSelected
            ? cellContextRef.current.selectedCells[sourceCell.cellId]?.events
            : [dragStateRef?.current?.item]

          events?.forEach(event => {
            moves.push({
              target: targetCell,
              source: sourceCell,
              sourceColumnId: sourceCell.columnId,
              targetColumnId: targetCell.columnId,
              sourceRowId: sourceCell.rowId,
              targetRowId: targetCell?.rowId,
              item: event,
            })
          })
        }
      })

      if (moves.length) {
        const ok = handleMoveCellsBetweenPositions(moves)

        if (ok && dragStateRef.current?.isBulk) {
          handleClearSelectedCells()
        }

        if (
          dropAction?.actionCode === ACTION_CODE.EXECUTE_COMMAND &&
          dropAction.command &&
          moves.length > 0
        ) {
          const command = dropAction.command
          const pkName = objectXPKFieldRef.current?.name

          for (const move of moves) {
            const itemId = pkName ? String(move.item[pkName]) : ''

            const commandBody = getCommandRequestData(command.body ?? [], move)
            const commandParams = getCommandRequestData(command.parameters ?? [], move)

            if (itemId) {
              dispatch(addLoadingItemId(itemId))
            }

            const reverseMove: TimelineCellMove = {
              item: move.item,
              sourceRowId: move.targetRowId,
              sourceColumnId: move.targetColumnId,
              targetRowId: move.sourceRowId,
              targetColumnId: move.sourceColumnId,
              source: move.source,
              target: move.target,
            }

            executeCommand({
              method: command.httpMethod,
              objectCode: command.objectCode,
              commandCode: command.name,
              isCommandResponse: command.isCommandResponse,
              params: commandParams,
              body: commandBody,
            })
              .unwrap()
              .catch(() => {
                handleMoveCellsBetweenPositions([reverseMove])
              })
              .finally(() => {
                if (itemId) {
                  dispatch(removeLoadingItemId(itemId))
                }
              })
          }
        }
      }
    }

    endDragSession()
  }

  const onEventMouseDown = (data: {
    e: ReactMouseEvent<HTMLDivElement>
    item: ObjectDataRecord
    rowId: string
    columnId: string
    rowData: ObjectDataRecord
    cellEvents: ObjectDataRecord[]
  }) => {
    const { rowId, columnId, e, item, rowData, cellEvents } = data

    if (e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) {
      return
    }

    const cellId = getCellId(rowId, columnId)

    e.preventDefault()

    const onMove = (ev: MouseEvent) => {
      const anchorCell = (e.target as HTMLElement).closest(
        `[${TIMELINE_CELL_DATA_ATTR}]`
      ) as HTMLElement | null

      if (!e || !anchorCell) {
        return
      }

      const dx = ev.clientX - e.clientX
      const dy = ev.clientY - e.clientY

      if (!dragStateRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return
      }

      if (!dragStateRef.current) {
        const isSelectedCell = Boolean(cellContextRef.current.selectedCells[cellId])

        const anchorRect = anchorCell.getBoundingClientRect()
        const ghostCells = buildGhostCells({
          anchorRect,
          anchorCell: {
            isSelected: isSelectedCell,
            rowId,
            columnId,
            events: cellEvents,
            item,
            rowData,
          },
        })

        const dragState: TimelineCellItemDragState = {
          item,
          sourceCellId: cellId,
          sourceRowId: rowId,
          sourceColumnId: columnId,
          isBulk: isSelectedCell,
          anchorOffsetX: ev.clientX - anchorRect.left,
          anchorOffsetY: ev.clientY - anchorRect.top,
          ghostCells,
          initialMouseX: ev.clientX,
          initialMouseY: ev.clientY,
        }

        dragStateRef.current = dragState
        dispatch(setCellItemDragState(dragState))
        applyDragBodyStyles()

        dragCellsRef.current.dragStartCell = {
          rowId,
          cellId,
          columnId,
          rowData,
        }
        cellContextRef.current = {
          ...cellContextRef.current,
          isDraggingActive: true,
        }

        const cells = isSelectedCell
          ? cellContextRef.current.selectedCells
          : {
              [cellId]: {
                rowId,
                columnId,
                events: cellEvents,
                item,
                rowData,
              } as SelectedCell,
            }

        for (const cellKey in cells) {
          const el = findCellElement(cellKey) as HTMLElement
          const cell = cells[cellKey]

          if (cell.events.length) {
            dragCellsRef.current?.source.push({
              htmlElement: el,
              cellId: cellKey,
              rowId: cell.rowId,
              columnId: cell.columnId,
              columnInfo: cell.rowData.columnsInfo[cell.columnId],
              isSelected: isSelectedCell,
              rowData: cell.rowData,
            })

            el.classList.add(TIME_LINE_OBJECT_X_CELL_DRAG_SOURCE_CLASS_NAME)
          }
        }
      }
    }

    const onMouseUp = () => {
      onDrop()
    }

    removeWindowListenersRef.current = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const onCellMouseEnter = (data: {
    event: ReactMouseEvent<HTMLDivElement>
    params: GridRenderCellParams
    rowId: string
    columnId: string
    cellId: string
  }) => {
    const { event, rowId, columnId, params, cellId } = data

    if (
      !dragCellsRef.current.source.length ||
      cellId === dragCellsRef.current.dragStartCell?.cellId
    ) {
      return
    }

    const prevCount = targetEnterCountRef.current.get(cellId) || 0
    targetEnterCountRef.current.set(cellId, prevCount + 1)

    if (prevCount === 0) {
      const active = dragStateRef.current
      const rowsObject = timeLineGridDataRef.current.reduce((previousValue, currentValue) => {
        return {
          ...previousValue,
          [currentValue.id]: currentValue,
        }
      }, {} as Record<string, TimeLineGridDataRecord>)

      if (active?.isBulk) {
        const allRowIds = timeLineGridDataRef.current.map(r => r.id)
        const dynamicColumnIds = cellContextRef.current.dynamicColumnIds || []

        const anchorRowIdx = allRowIds.indexOf(active.sourceRowId)
        const targetRowIdx = allRowIds.indexOf(rowId)
        const anchorColIdx = dynamicColumnIds.indexOf(active.sourceColumnId)
        const targetColIdx = dynamicColumnIds.indexOf(columnId)

        if (anchorRowIdx < 0 || targetRowIdx < 0 || anchorColIdx < 0 || targetColIdx < 0) {
          return
        }

        const rowDelta = targetRowIdx - anchorRowIdx
        const colDelta = targetColIdx - anchorColIdx

        for (const selected of dragCellsRef.current.source) {
          const rIdx = allRowIds.indexOf(selected.rowId)
          const cIdx = dynamicColumnIds.indexOf(selected.columnId)

          if (rIdx < 0 || cIdx < 0) {
            continue
          }

          const newRIdx = rIdx + rowDelta
          const newCIdx = cIdx + colDelta

          if (
            newRIdx < 0 ||
            newRIdx >= allRowIds.length ||
            newCIdx < 0 ||
            newCIdx >= dynamicColumnIds.length
          ) {
            dragCellsRef.current.target.push(null)

            continue
          }

          const targetCellId = getCellId(allRowIds[newRIdx], dynamicColumnIds[newCIdx])

          const el = findCellElement(targetCellId)

          if (el) {
            el.classList.add(TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_CLASS_NAME)
          }

          dragCellsRef.current.target.push({
            cellId: targetCellId,
            columnId: dynamicColumnIds[newCIdx],
            rowId: allRowIds[newRIdx],
            columnInfo: params.row.columnsInfo?.[dynamicColumnIds[newCIdx]],
            htmlElement: el,
            rowData: rowsObject[allRowIds[newRIdx]],
          })
        }
      } else {
        const el = event.target as HTMLElement

        el.classList.add(TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_CLASS_NAME)

        dragCellsRef.current.target.push({
          cellId,
          columnId,
          rowId,
          columnInfo: params.row.columnsInfo?.[columnId],
          htmlElement: el,
          rowData: rowsObject[rowId],
        })
      }
    }
  }

  const onCellMouseLeave = (e: ReactMouseEvent<HTMLDivElement>, cellId: string) => {
    if (!dragStateRef.current) {
      return
    }

    const related = e.relatedTarget as Node | null

    if (related && (e.currentTarget as HTMLElement).contains(related)) {
      return
    }

    const current = targetEnterCountRef.current.get(cellId) ?? 0

    if (current <= 0) {
      return
    }

    const next = current - 1

    if (next <= 0) {
      targetEnterCountRef.current.delete(cellId)

      for (const item of dragCellsRef.current.target) {
        item?.htmlElement?.classList.remove(TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_CLASS_NAME)
      }

      dragCellsRef.current.target = []
    } else {
      targetEnterCountRef.current.set(cellId, next)
    }
  }

  const onCellMouseUp = () => {
    onDrop()
  }

  useEffect(() => {
    return () => {
      removeWindowListenersRef.current?.()
      restoreAllCellStyles()
      restoreDragBodyStyles()
    }
  }, [])

  return {
    cellItemDrag: {
      onEventMouseDown,
      onCellMouseEnter,
      onCellMouseLeave,
      onCellMouseUp,
    },
  }
}
